/**
 * Browser audio pipeline for voice calls, trimmed from wixel-fs's audio-pipeline.
 *
 *   - `MicCapture`: getUserMedia → AudioWorklet → base64 16kHz Int16 PCM.
 *   - `PcmPlayback`: gapless scheduled playback of 24kHz PCM; `stop()` is
 *     the barge-in path.
 */

import { applyPreferredSink } from './voice-audio-prefs';
// The same threshold the silence watch judges a live microphone by, so "the user said nothing"
// means one thing across the whole voice path.
import { AUDIBLE_RMS } from './voice-silence';

// Inlined AudioWorkletProcessor, shipped as a Blob URL so we don't host a
// static file. Capture runs on the audio thread — a main-thread
// ScriptProcessorNode stalls during React renders/GC and drops mic audio.
const PCM_WORKLET_SOURCE = `
class PcmProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._buffer = new Int16Array(2048);
    this._offset = 0;
  }
  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0]) return true;
    const f32 = input[0];
    for (let i = 0; i < f32.length; i++) {
      this._buffer[this._offset++] = Math.max(-32768, Math.min(32767, (f32[i] || 0) * 32768));
      if (this._offset >= this._buffer.length) {
        // postMessage structured-clones the typed array, so reusing the
        // same backing buffer next quantum is safe.
        this.port.postMessage(this._buffer);
        this._offset = 0;
      }
    }
    return true;
  }
}
registerProcessor('pcm-processor', PcmProcessor);
`;

/**
 * What Gemini Live wants from the microphone. Providers differ here — Grok's realtime API takes
 * 24 kHz in — so the rate is a MicCapture constructor argument and this is only its default.
 * Playback is 24 kHz for both, so `PLAYBACK_SAMPLE_RATE` stays a constant.
 */
export const DEFAULT_MIC_SAMPLE_RATE = 16000;
const PLAYBACK_SAMPLE_RATE = 24000;
const MIC_BUFFER_CHUNK_CAP = 80; // ~10s of pre-connect speech; bounds memory if the WS never opens

/**
 * Chromium lists the OS default input as a real device with this id (labelled "Default - …").
 * It is NOT the same thing as sending no deviceId constraint at all — see `preferredInputId`.
 */
export const SYSTEM_DEFAULT_INPUT_ID = 'default';

/**
 * Which deviceId constraint to open the microphone with.
 *
 * "System default" in our picker means "no remembered id", and the obvious implementation —
 * send no deviceId and let the browser choose — is what shipped, and it produced calls the
 * agent could not hear. An unconstrained `getUserMedia` lets Chrome resolve the input however
 * it likes, and on macOS it resolved to a device that delivered pure silence; opening the
 * picker and choosing the entry literally labelled "Default - Built-in Microphone" fixed it
 * instantly. That entry is a REAL device (`deviceId === 'default'`) which tracks whatever the
 * OS default currently is — it is the thing the user means by "system default", and the
 * unconstrained request is not it.
 */
export async function preferredInputId(remembered: string | null): Promise<string | null> {
  if (remembered) return remembered;
  try {
    const devices = await navigator?.mediaDevices?.enumerateDevices?.();
    const hasDefault = (devices ?? []).some(
      (d) => d.kind === 'audioinput' && d.deviceId === SYSTEM_DEFAULT_INPUT_ID,
    );
    return hasDefault ? SYSTEM_DEFAULT_INPUT_ID : null;
  } catch {
    // enumerateDevices is unavailable or refused — fall back to the unconstrained request.
    return null;
  }
}

/**
 * Captures mic audio as base64 16-bit PCM chunks. Chunks captured between
 * "user clicked Call" and "WebSocket ready" accumulate in a buffer and flush
 * on `drain()` — without this the first ~half second of speech is lost to
 * the WS handshake and the agent opens with "Sorry, what?".
 */
export class MicCapture {
  private onChunk: ((b64: string) => void) | null = null;
  private onLevel: ((rms: number) => void) | null = null;
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private worklet: AudioWorkletNode | null = null;
  // Visualization-only tap. The worklet posts one RMS per 2048-sample chunk
  // (~128 ms / ~8 Hz at 16 kHz) — fine for silence detection, far too coarse to
  // drive a 60 fps orb, which then lurches once per chunk instead of tracking the
  // voice. This analyser is read FRESH every animation frame, exactly like the
  // reference `Voice Screen.html`, giving a continuous level. It never touches the
  // send path.
  private analyser: AnalyserNode | null = null;
  private levelBuf: Uint8Array<ArrayBuffer> | null = null;
  private buffer: { b64: string; audible: boolean }[] = [];
  private buffering = true;
  private muted = false;
  private stopped = false;
  private deviceId: string | null = null;
  private sampleRate: number;

  constructor(sampleRate: number = DEFAULT_MIC_SAMPLE_RATE) {
    this.sampleRate = sampleRate;
  }

  async start(
    onChunk: (b64: string) => void,
    onLevel?: (rms: number) => void,
    deviceId?: string | null,
  ): Promise<void> {
    this.onChunk = onChunk;
    this.onLevel = onLevel ?? null;
    this.buffer = [];
    this.buffering = true;
    this.deviceId = deviceId ?? null;
    await this.openStream(this.deviceId);
  }

  /**
   * Switch input device mid-call.
   *
   * The capture graph is getUserMedia → AudioContext → AudioWorklet, so this rebuilds it rather
   * than swapping a track. If the new device fails, the PREVIOUS one is re-opened and the error
   * carries `rolledBack` — the caller should stay quiet about that case, because the user is
   * still talking on a working mic and a red banner would be a lie.
   */
  async setDevice(deviceId: string | null): Promise<void> {
    if (this.stopped || deviceId === this.deviceId) return;
    const previous = this.deviceId;
    const wasBuffering = this.buffering;
    this.teardownGraph();
    try {
      await this.openStream(deviceId);
      this.deviceId = deviceId;
      this.buffering = wasBuffering;
    } catch (err) {
      try {
        await this.openStream(previous);
        this.buffering = wasBuffering;
        Object.assign(err as object, { rolledBack: true });
      } catch {
        // Both failed — the call genuinely has no microphone now.
      }
      throw err;
    }
  }

  /**
   * Re-open capture at a different sample rate.
   *
   * The microphone is deliberately started BEFORE the token is minted, so the permission prompt
   * appears immediately and nothing said during the handshake is lost — which means capture begins
   * before we know which provider (and therefore which rate) the call will use. Gemini wants
   * 16 kHz and Grok 24 kHz, and capturing at one while declaring the other ships audio at the wrong
   * pitch. So the rate is corrected here once the credential names the provider: a no-op in the
   * common case, and an inaudible graph rebuild otherwise, since permission has already been
   * granted and anything buffered stays buffered.
   */
  async setSampleRate(sampleRate: number): Promise<void> {
    if (this.stopped || sampleRate === this.sampleRate) return;
    const previous = this.sampleRate;
    const wasBuffering = this.buffering;
    this.teardownGraph();
    this.sampleRate = sampleRate;
    try {
      await this.openStream(this.deviceId);
    } catch (err) {
      this.sampleRate = previous;
      await this.openStream(this.deviceId).catch(() => {
        // Both failed — the call genuinely has no microphone now; start() will surface it.
      });
      throw err;
    } finally {
      this.buffering = wasBuffering;
    }
  }

  private async openStream(deviceId: string | null): Promise<void> {
    const constraints = (id: MediaTrackConstraints['deviceId']): MediaStreamConstraints => ({
      audio: {
        ...(id ? { deviceId: id } : {}),
        sampleRate: this.sampleRate,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    // `exact` for BOTH a remembered device and the resolved system default, with the fallback below
    // as the safety net.
    //
    // `ideal` is only a HINT: a Chrome that declines it picks the mic itself, which is the
    // unconstrained request that delivers pure silence. An `exact` device that is gone throws
    // OverconstrainedError, which is caught below and retried unconstrained.
    const preferred = await preferredInputId(deviceId);
    const wanted: MediaTrackConstraints['deviceId'] | undefined = preferred
      ? { exact: preferred }
      : undefined;

    // `stop()` can land at ANY await in this method — the permission prompt alone sits there for as
    // long as the user ignores it — and until the very last lines NOTHING built here is reachable
    // from `this`: `stop()` nulls fields that are still local variables at this point. So the call
    // ends, the UI says it ended, and the browser's recording indicator stays LIT for the rest of
    // the page's life on a stream nobody holds a reference to — which is also how the NEXT call
    // meets `NotReadableError` on a device this tab never let go of (ORION-WEB-1V). Every await
    // below is therefore followed by the same question, and the answer releases what it opened.
    const abandon = (openedStream: MediaStream | null, openedContext: AudioContext | null): void => {
      for (const track of openedStream?.getTracks() ?? []) track.stop();
      openedContext?.close().catch(() => {});
    };

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(constraints(wanted));
    } catch (err) {
      // A remembered deviceId from another machine or browser profile doesn't exist here, and an
      // `exact` constraint makes that fatal. Falling back to the default is far better than
      // refusing to start a call. Duck-typed because not every environment subclasses Error.
      const name = (err as { name?: string } | null)?.name;
      if (wanted && name === 'OverconstrainedError') {
        stream = await navigator.mediaDevices.getUserMedia(constraints(undefined));
      } else {
        throw err;
      }
    }

    // Second half of the same bug, and the reason the first fix did not hold: BEFORE mic permission
    // is granted, `enumerateDevices()` reports entries with BLANK deviceIds, so `preferredInputId`
    // cannot see the `'default'` entry and returns null — we send no constraint at all, which is the
    // silence case. Permission is granted by the call above, so now the entry is visible: if we
    // opened unconstrained and a default device does exist, reopen on it exactly.
    //
    // Costs one extra getUserMedia only on the first call of a fresh grant, and never runs when a
    // device was remembered or already resolved.
    if (!wanted) {
      const resolved = await preferredInputId(null);
      if (resolved) {
        try {
          const onDefault = await navigator.mediaDevices.getUserMedia(
            constraints({ exact: resolved }),
          );
          stream.getTracks().forEach((track) => track.stop());
          stream = onDefault;
        } catch {
          // Keep the working unconstrained stream: a call with imperfect routing beats no call.
        }
      }
    }

    if (this.stopped) return abandon(stream, null);

    const audioContext = new AudioContext({ sampleRate: this.sampleRate });
    await audioContext.resume();
    if (this.stopped) return abandon(stream, audioContext);

    const blob = new Blob([PCM_WORKLET_SOURCE], { type: 'application/javascript' });
    const blobUrl = URL.createObjectURL(blob);
    try {
      await audioContext.audioWorklet.addModule(blobUrl);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
    if (this.stopped) return abandon(stream, audioContext);

    const source = audioContext.createMediaStreamSource(stream);
    const worklet = new AudioWorkletNode(audioContext, 'pcm-processor');
    source.connect(worklet);
    // The processor writes nothing to its outputs, so this connection is
    // silent — it just keeps the node in the active audio graph.
    worklet.connect(audioContext.destination);

    // Continuous level tap for the orb (see `analyser` field). Time-domain RMS,
    // so `smoothingTimeConstant` (frequency-only) is immaterial; the 1024-sample
    // window read per frame mirrors the reference. Not connected onward — an
    // AnalyserNode observes its input without needing an output edge.
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.65;
    source.connect(analyser);
    this.analyser = analyser;
    this.levelBuf = new Uint8Array(analyser.fftSize);

    worklet.port.onmessage = (event: MessageEvent) => {
      if (this.stopped) return;
      const samples = event.data as Int16Array;
      const rms = this.muted ? 0 : rmsLevel(samples);
      this.onLevel?.(rms);
      if (this.muted) return;
      const b64 = int16ToBase64(samples);
      if (this.buffering) {
        if (this.buffer.length < MIC_BUFFER_CHUNK_CAP) {
          this.buffer.push({ b64, audible: rms >= AUDIBLE_RMS });
        }
        return;
      }
      this.onChunk?.(b64);
    };

    this.stream = stream;
    this.audioContext = audioContext;
    this.source = source;
    this.worklet = worklet;
  }

  private teardownGraph(): void {
    if (this.worklet) {
      this.worklet.port.onmessage = null;
      this.worklet.disconnect();
      this.worklet = null;
    }
    this.analyser?.disconnect();
    this.analyser = null;
    this.levelBuf = null;
    this.source?.disconnect();
    this.source = null;
    this.audioContext?.close().catch(() => {});
    this.audioContext = null;
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
  }

  /**
   * Instantaneous input level (RMS, 0..1) sampled fresh for 60 fps visualization.
   * Continuous — unlike the ~8 Hz chunk RMS the send path reports — so the orb
   * tracks the voice smoothly. Returns `null` when no analyser is up (before
   * permission, or an unsupported browser) so callers fall back to the chunk
   * value, and 0 while muted so the orb reads silent like the rest of the UI.
   */
  liveLevel(): number | null {
    if (this.muted) return 0;
    const analyser = this.analyser;
    const buf = this.levelBuf;
    if (!analyser || !buf) return null;
    analyser.getByteTimeDomainData(buf);
    let sumSquares = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = ((buf[i] ?? 128) - 128) / 128;
      sumSquares += v * v;
    }
    return Math.sqrt(sumSquares / buf.length);
  }

  /**
   * Flush buffered SPEECH in order, then stream live.
   *
   * Everything before the first audible chunk is dropped, and a buffer with nothing audible in it
   * is dropped whole. The buffer exists so the handshake eats no speech — it was never meant to
   * deliver the several seconds of room tone that a caller who clicked Call and waited quietly
   * inevitably produces. Delivering it is actively harmful: drain lands at the exact moment the
   * greeting turn is generating, so the model receives what looks like the user talking over its
   * own hello, and answers it — which is the doubled greeting ("I heard a greeting message and
   * then another one immediately"). On a deaf microphone it is worse than doubled: an all-zero
   * buffer is what Gemini's ASR hallucinated Japanese and Italian out of.
   *
   * Chunks after the first audible one are kept even when quiet — the gaps between words are part
   * of the sentence, and re-gating mid-utterance would clip it.
   */
  drain(): void {
    const chunks = this.buffer;
    this.buffer = [];
    this.buffering = false;
    if (this.muted) return;
    const speechStart = chunks.findIndex((chunk) => chunk.audible);
    if (speechStart < 0) {
      if (chunks.length) {
        console.warn(`[voice] dropped ${chunks.length} inaudible pre-connect chunks`);
      }
      return;
    }
    for (const chunk of chunks.slice(speechStart)) this.onChunk?.(chunk.b64);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  stop(): void {
    this.stopped = true;
    if (this.worklet) {
      this.worklet.port.onmessage = null;
      this.worklet.disconnect();
      this.worklet = null;
    }
    this.analyser?.disconnect();
    this.analyser = null;
    this.levelBuf = null;
    this.source?.disconnect();
    this.source = null;
    this.audioContext?.close().catch(() => {});
    this.audioContext = null;
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
  }
}

/**
 * Schedules base64-decoded PCM chunks for gapless playback. The
 * AudioContext is created lazily on the first chunk so we don't hold an
 * audio device before the model actually speaks.
 */
export class PcmPlayback {
  private audioContext: AudioContext | null = null;
  private gain: GainNode | null = null;
  private nextPlayTime = 0;
  private activeSources = new Set<AudioBufferSourceNode>();
  private closed = false;
  private lastChunkLevel = 0;
  private volume = 1;
  /** Chosen output device, applied lazily if the context doesn't exist yet. */
  private sinkId: string | null = null;

  /**
   * Output volume, 0..1. Applied straight to the gain node — no graph rebuild, so it takes effect
   * mid-sentence.
   */
  setVolume(volume: number): void {
    const next = Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : 1;
    this.volume = next;
    if (this.gain) this.gain.gain.value = next;
  }

  getVolume(): number {
    return this.volume;
  }

  /**
   * Route playback to a specific output device. `null` = system default.
   *
   * This is `AudioContext.setSinkId` — Chromium 110+ — NOT the HTMLMediaElement method, so it
   * works directly on the Web Audio graph with no media element and no rebuild. Elsewhere it is
   * simply absent, which is why the picker is disabled rather than broken (see outputDeviceSupported).
   */
  async setOutputDevice(deviceId: string | null): Promise<void> {
    this.sinkId = deviceId;
    await this.applySinkId();
  }

  private async applySinkId(): Promise<void> {
    // Resolve through the shared preferred-sink logic: a remembered id is used as-is, and an
    // unremembered one prefers the real 'default' output entry over the unconstrained '' sink —
    // the speaker twin of preferredInputId. Shared with the spoken brief so both agree on the device.
    await applyPreferredSink(this.audioContext, this.sinkId);
  }

  /** True while model audio is scheduled or playing. */
  isPlaying(): boolean {
    return this.activeSources.size > 0;
  }

  /** Rough output level for visualization: last chunk's RMS while playing. */
  getLevel(): number {
    return this.activeSources.size > 0 ? this.lastChunkLevel : 0;
  }

  enqueue(raw: Uint8Array): void {
    if (this.closed || typeof AudioContext === 'undefined') return;
    if (!this.audioContext) {
      this.audioContext = new AudioContext({ sampleRate: PLAYBACK_SAMPLE_RATE });
      this.audioContext.resume().catch(() => {});
      this.gain = this.audioContext.createGain();
      this.gain.gain.value = this.volume;
      this.gain.connect(this.audioContext.destination);
      this.nextPlayTime = 0;
      if (this.sinkId !== null) void this.applySinkId();
    }
    const audioContext = this.audioContext;
    const samples = new Int16Array(raw.buffer, raw.byteOffset, raw.byteLength / 2);
    this.lastChunkLevel = rmsLevel(samples);
    const buf = audioContext.createBuffer(1, samples.length, PLAYBACK_SAMPLE_RATE);
    const channel = buf.getChannelData(0);
    for (let i = 0; i < samples.length; i++) channel[i] = (samples[i] ?? 0) / 32768;
    const source = audioContext.createBufferSource();
    source.buffer = buf;
    source.connect(this.gain ?? audioContext.destination);
    const startAt = Math.max(audioContext.currentTime, this.nextPlayTime);
    this.activeSources.add(source);
    source.onended = () => {
      this.activeSources.delete(source);
      source.disconnect();
    };
    source.start(startAt);
    this.nextPlayTime = startAt + buf.duration;
  }

  /**
   * Play a short tone through the SELECTED output device.
   *
   * The point is to answer "is my speaker right?" without starting a call. It deliberately runs
   * through this same graph — gain node and sink — so what you hear is exactly the path the
   * agent's voice takes; a tone played through a fresh AudioContext would prove nothing about the
   * device actually chosen. Two soft notes rather than a beep, so it reads as the product and not
   * a hardware diagnostic.
   */
  async playTestTone(): Promise<void> {
    if (this.closed || typeof AudioContext === 'undefined') return;
    if (!this.audioContext) {
      this.audioContext = new AudioContext({ sampleRate: PLAYBACK_SAMPLE_RATE });
      this.gain = this.audioContext.createGain();
      this.gain.gain.value = this.volume;
      this.gain.connect(this.audioContext.destination);
      if (this.sinkId !== null) await this.applySinkId();
    }
    const ctx = this.audioContext;
    await ctx.resume().catch(() => {});
    const out: AudioNode = this.gain ?? ctx.destination;
    const now = ctx.currentTime;
    // A rising two-note figure: unmistakably "the speaker works", short enough not to annoy.
    for (const { freq, at, dur } of [
      { freq: 587.33, at: 0, dur: 0.18 },
      { freq: 880, at: 0.16, dur: 0.26 },
    ]) {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      // Ramped, never a click: an abrupt full-amplitude sine is unpleasant on headphones.
      env.gain.setValueAtTime(0, now + at);
      env.gain.linearRampToValueAtTime(0.22, now + at + 0.03);
      env.gain.exponentialRampToValueAtTime(0.0001, now + at + dur);
      osc.connect(env);
      env.connect(out);
      osc.start(now + at);
      osc.stop(now + at + dur + 0.02);
    }
  }

  /**
   * Barge-in: cancel everything queued. Snapshots the set first because
   * `source.stop()` can synchronously fire `onended`, which mutates the set
   * mid-iteration.
   */
  stop(): void {
    const snapshot = Array.from(this.activeSources);
    this.activeSources.clear();
    for (const source of snapshot) {
      try {
        source.stop();
      } catch {
        // Already stopped or never started.
      }
      source.disconnect();
    }
    this.nextPlayTime = 0;
  }

  async close(): Promise<void> {
    this.closed = true;
    this.stop();
    const audioContext = this.audioContext;
    this.audioContext = null;
    if (audioContext) {
      try {
        await audioContext.close();
      } catch {
        // Some environments reject AudioContext.close().
      }
    }
  }
}

/** Decode base64 → bytes. Browser-only — no Buffer in client code. */
export function base64ToUint8Array(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function rmsLevel(samples: Int16Array): number {
  let sumSquares = 0;
  for (let i = 0; i < samples.length; i++) {
    const v = (samples[i] ?? 0) / 32768;
    sumSquares += v * v;
  }
  return Math.sqrt(sumSquares / samples.length);
}

function int16ToBase64(samples: Int16Array): string {
  const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/**
 * Anchor the autoplay user-gesture: call SYNCHRONOUSLY at the top of the
 * click handler, before any await. Forces Chrome to record sticky user
 * activation so AudioContexts created later (after awaits) don't come up
 * permanently `suspended`.
 */
export function primeAudioGesture(): void {
  if (typeof AudioContext === 'undefined') return;
  try {
    const audioContext = new AudioContext();
    // The rejection is HANDLED rather than `void`ed, because the close below is racing this resume
    // on purpose: the context exists only to record the gesture, so it is thrown away on the next
    // tick whether or not it ever finished resuming. Firefox rejects a resume that is still pending
    // when its context closes — `InvalidStateError: Closed before resume completed` — which made
    // every tap of Speak an unhandled rejection in Sentry (ORION-WEB-24). Priming has already
    // happened by then; the outcome of the resume is not information anyone needs.
    audioContext.resume().catch(() => {});
    setTimeout(() => {
      audioContext.close().catch(() => {});
    }, 0);
  } catch {
    // Sticky activation may already be set; fail open.
  }
}

/**
 * The clock the "thinking" cue keeps. Shared 1:1 with iOS (`VoiceThinkingCue` in
 * `VoiceAudioEngine.swift`) and Android (`thinkingCueCyclePcm` in `VoiceAudio.kt`) — change one and
 * change all three, or the same wait sounds different depending on which app you are holding.
 *
 * The 3007:1803 ratio is 5:3, a major sixth, and that consonant interval IS the character. Rounding
 * the frequencies stops it sounding musical. Pure sines only: noise, a second partial and a filter
 * were each tried and each made it mechanical rather than clock-like.
 */
export const THINKING_CUE = {
  /** Seconds between strikes — so a full tick-tock cycle is two of them. */
  intervalSec: 1,
  /**
   * Silence before the FIRST strike, and again after every pause. A wait shorter than this makes no
   * sound at all — the cue exists for the silence, not for the wait.
   *
   * TWO seconds, not one, because the agent's own spoken filler ("one sec, let me check") is the
   * primary signal and the tick is only its fallback: the model is freed to speak by the ask's
   * `answered:false` ack and starts within a beat, so a one-second grace fired a strike FIRST and
   * the user heard the cue announce a wait the agent then announced again. Two seconds clears the
   * model's own reaction time, and an ask's p90 is ~29s (see ASK_SLOW_NUDGE_MS) — nothing that
   * actually needs filling is anywhere near this floor.
   */
  graceSec: 2,
  /** Even strikes are the "tick", odd ones the "tock". */
  strikes: [
    { frequency: 3007, peak: 0.1 },
    { frequency: 1803, peak: 0.095 },
  ],
  /** Instant attack, then an exponential decay with this time constant… */
  decayTauSec: 0.0184,
  /** …truncated here… */
  decaySec: 0.016,
  /** …and released linearly over this, which is what keeps the truncation from clicking. */
  releaseSec: 0.0015,
} as const;

/**
 * Whether the tick-tock should be sounding right now.
 *
 * The cue fills a wait the agent is NOT already filling itself. While the agent speaks — its own
 * "one sec" filler, a progress line, or the answer being read out — the user is being told what is
 * happening by the better signal, and ticking underneath it is a second voice saying the same thing.
 * So the cue pauses while the agent talks and resumes (on a fresh grace) if the silence returns,
 * which it does: an ask outlives any filler by tens of seconds.
 *
 * Shared 1:1 with iOS (`VoiceAudioEngine.thinkingCueActive`) and Android (`VoicePlayback`).
 */
export function thinkingCueActive(working: boolean, agentSpeaking: boolean): boolean {
  return working && !agentSpeaking;
}

/** Which strike lands at index `n` of the grid, counting from the first one. */
export function thinkingStrike(index: number): { frequency: number; peak: number } {
  const strikes = THINKING_CUE.strikes;
  return strikes[((index % strikes.length) + strikes.length) % strikes.length]!;
}

/**
 * How far ahead of the audio clock strikes are placed, and how often that is topped up.
 *
 * The window must clear the browser's BACKGROUND-TAB timer clamp (≥1 s), not just the top-up
 * interval: a call minimized behind another tab keeps its audio running but has its `setInterval`
 * throttled, and a lookahead under a second would then place strikes late or miss them entirely.
 */
const CUE_LOOKAHEAD_SEC = 1.5;
const CUE_SCHEDULER_INTERVAL_MS = 150;
/** Long enough that cutting a sounding strike doesn't click, short enough to read as instant. */
const CUE_FADE_SEC = 0.02;

/**
 * A clock tick-tock played while a tool call is in flight, so the blocking wait is never dead
 * silence.
 *
 * Strikes are placed on the AudioContext's own clock rather than fired from a `setInterval` — timer
 * drift is plainly audible on a one-second grid. A repeating timer only tops the schedule up; every
 * strike time is derived from the first one, so the grid can never accumulate error.
 *
 * Lazily creates its own AudioContext (separate from playback) on first start; the click that began
 * the call already primed audio so it can resume.
 */
export class ThinkingCue {
  private audioContext: AudioContext | null = null;
  private gain: GainNode | null = null;
  /** Strikes placed on the timeline but not yet finished — what `stop()` has to take back. */
  private scheduled = new Set<OscillatorNode>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private firstStrikeAt = 0;
  private nextIndex = 0;
  private active = false;

  start(): void {
    if (this.active || typeof AudioContext === 'undefined') return;
    this.active = true;
    const ctx = this.audioContext ?? new AudioContext();
    this.audioContext = ctx;
    ctx.resume().catch(() => {});
    const gain = ctx.createGain();
    gain.gain.value = 1;
    gain.connect(ctx.destination);
    this.gain = gain;
    this.firstStrikeAt = ctx.currentTime + THINKING_CUE.graceSec;
    this.nextIndex = 0;
    this.fillSchedule();
    this.timer = setInterval(() => this.fillSchedule(), CUE_SCHEDULER_INTERVAL_MS);
  }

  /** Place every strike whose time has come within the lookahead window. */
  private fillSchedule(): void {
    const ctx = this.audioContext;
    const gain = this.gain;
    if (!this.active || !ctx || !gain) return;
    const horizon = ctx.currentTime + CUE_LOOKAHEAD_SEC;
    while (this.firstStrikeAt + this.nextIndex * THINKING_CUE.intervalSec <= horizon) {
      this.strike(ctx, gain, this.firstStrikeAt + this.nextIndex * THINKING_CUE.intervalSec, this.nextIndex);
      this.nextIndex += 1;
    }
  }

  private strike(ctx: AudioContext, out: GainNode, at: number, index: number): void {
    const { frequency, peak } = thinkingStrike(index);
    const { decaySec, decayTauSec, releaseSec } = THINKING_CUE;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    // Instant attack — a ramp up would soften the strike into a chime.
    env.gain.setValueAtTime(peak, at);
    env.gain.exponentialRampToValueAtTime(peak * Math.exp(-decaySec / decayTauSec), at + decaySec);
    env.gain.linearRampToValueAtTime(0.00001, at + decaySec + releaseSec);
    osc.connect(env);
    env.connect(out);
    osc.start(at);
    osc.stop(at + decaySec + releaseSec);
    this.scheduled.add(osc);
    osc.onended = () => {
      this.scheduled.delete(osc);
      osc.disconnect();
      env.disconnect();
    };
  }

  /**
   * Stop the cue AND un-schedule the strikes already placed ahead of the clock. Fading the top gain
   * alone is not enough — those nodes would keep their own envelopes and the ticking would carry on
   * under a muted bus, which is exactly the bug an earlier attempt shipped.
   */
  stop(): void {
    if (!this.active) return;
    this.active = false;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const ctx = this.audioContext;
    const gain = this.gain;
    this.gain = null;
    const pending = Array.from(this.scheduled);
    this.scheduled.clear();
    const t = ctx?.currentTime ?? 0;
    if (gain) {
      gain.gain.cancelScheduledValues(t);
      gain.gain.setValueAtTime(gain.gain.value, t);
      gain.gain.linearRampToValueAtTime(0, t + CUE_FADE_SEC);
    }
    for (const osc of pending) {
      try {
        // A stop time before the node's start time means it never sounds at all — which is how a
        // strike scheduled seconds into the future is cancelled.
        osc.stop(t + CUE_FADE_SEC);
      } catch {
        // Already finished.
      }
    }
    if (gain) setTimeout(() => gain.disconnect(), CUE_FADE_SEC * 1000 + 40);
  }

  close(): void {
    this.stop();
    const ctx = this.audioContext;
    this.audioContext = null;
    if (ctx) setTimeout(() => ctx.close().catch(() => {}), 300);
  }
}
